import { PeopleSdk } from 'lib-klab-sdks';

type PagedEmployeeListResponse = PeopleSdk.PagedEmployeeListResponse;
type EmployeeListResponse = PeopleSdk.EmployeeListResponse;
type EmployeeResponse = PeopleSdk.EmployeeResponse;
type EmployeeCreateRequest = PeopleSdk.EmployeeCreateRequest;
type EmployeeUpdateRequest = PeopleSdk.EmployeeUpdateRequest;
type EmployeeWorkDayInternalResponse = PeopleSdk.EmployeeWorkDayInternalResponse;

function buildPage(number: number, size: number, totalElements: number): PeopleSdk.PaginationResponse {
    return {
        number,
        size,
        totalElements,
        totalPages: Math.max(1, Math.ceil(totalElements / size)),
    };
}

function deepClone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}

const loc = (id: number, name: string): PeopleSdk.LocationObject => ({ locationId: id, name });
const dept = (id: number, name: string): PeopleSdk.DepartmentObject => ({ departmentId: id, name });
const role = (id: number, name: string): PeopleSdk.RoleObject => ({ roleId: id, name });

const defaultEmployees: EmployeeListResponse[] = [
    { employeeId: 1, code: 'EMP001', name: 'Juan', lastname: 'Pérez', email: 'juan.perez@example.com', timezone: 'America/Lima', location: loc(1, 'Lima HQ'), department: dept(1, 'RRHH'), role: role(1, 'Líder'), schedule: {} as any },
    { employeeId: 2, code: 'EMP002', name: 'María', lastname: 'García', email: 'maria.garcia@example.com', timezone: 'America/Lima', location: loc(1, 'Lima HQ'), department: dept(1, 'RRHH'), role: role(2, 'Colaborador'), schedule: {} as any },
    { employeeId: 3, code: 'EMP003', name: 'Carlos', lastname: 'López', email: 'carlos.lopez@example.com', timezone: 'America/Lima', location: loc(2, 'Arequipa'), department: dept(2, 'Operaciones'), role: role(1, 'Líder'), schedule: {} as any },
    { employeeId: 4, code: 'EMP004', name: 'Ana', lastname: 'Martínez', email: 'ana.martinez@example.com', timezone: 'America/Lima', location: loc(1, 'Lima HQ'), department: dept(3, 'Finanzas'), role: role(2, 'Colaborador'), schedule: {} as any },
    { employeeId: 5, code: 'EMP005', name: 'Luis', lastname: 'Rodríguez', email: 'luis.rodriguez@example.com', timezone: 'America/Lima', location: loc(2, 'Arequipa'), department: dept(2, 'Operaciones'), role: role(2, 'Colaborador'), schedule: {} as any },
];

class EmployeesSdkMockStore {
    private employees: EmployeeListResponse[] = deepClone(defaultEmployees);
    private nextId = 100;

    getAllEmployees(
        page = 0,
        size = 10,
        _locationId?: number,
        _departmentId?: number,
        _roleId?: number,
    ): PagedEmployeeListResponse {
        const p = Math.max(0, page);
        const s = Math.max(1, size);
        const total = this.employees.length;
        const start = p * s;
        const content = this.employees.slice(start, start + s);
        return {
            content: deepClone(content),
            page: buildPage(p, s, total),
        };
    }

    getEmployeeById(employeeId: number): EmployeeResponse {
        const emp = this.employees.find((e) => e.employeeId === employeeId);
        if (!emp) throw { status: 404, message: 'Empleado no encontrado' };
        return deepClone({
            ...emp,
            status: 'ACTIVE',
            createdAt: '2024-01-01T00:00:00',
            updatedAt: '2024-01-01T00:00:00',
        } as EmployeeResponse);
    }

    createEmployee(payload: EmployeeCreateRequest): EmployeeResponse {
        const code = `GEN${this.nextId}`;
        const newEmp: EmployeeListResponse = {
            employeeId: this.nextId++,
            code,
            name: String(payload?.name ?? '').trim() || 'Nuevo',
            lastname: String(payload?.lastname ?? '').trim() || 'Empleado',
            email: String(payload?.email ?? '').trim() || `${code.toLowerCase()}@example.com`,
            timezone: 'America/Lima',
            location: loc(payload?.locationId ?? 1, 'Lima HQ'),
            department: dept(payload?.departmentId ?? 1, 'RRHH'),
            role: role(payload?.roleId ?? 2, 'Colaborador'),
            schedule: {} as any,
        };
        this.employees.push(newEmp);
        return this.getEmployeeById(newEmp.employeeId);
    }

    updateEmployee(_employeeId: number, _payload: EmployeeUpdateRequest): void {
    }

    deleteEmployeeById(employeeId: number): void {
        const idx = this.employees.findIndex((e) => e.employeeId === employeeId);
        if (idx >= 0) this.employees.splice(idx, 1);
    }

    getEmployeeWorkDaysById(employeeId: number): EmployeeWorkDayInternalResponse {
        const emp = this.employees.find((e) => e.employeeId === employeeId);
        if (!emp) throw { status: 404, message: 'Empleado no encontrado' };
        return {
            employee: { employeeId: emp.employeeId, code: emp.code, name: emp.name, lastname: emp.lastname, email: emp.email } as any,
            workDays: [],
        };
    }
}

const store = new EmployeesSdkMockStore();

export const employeesSdkMock = {
    getAllEmployees: (page?: number, size?: number, locationId?: number, departmentId?: number, roleId?: number) =>
        store.getAllEmployees(page ?? 0, size ?? 10, locationId, departmentId, roleId),
    getEmployeeById: (employeeId: number) => store.getEmployeeById(employeeId),
    createEmployee: (payload: EmployeeCreateRequest) => store.createEmployee(payload),
    updateEmployee: (employeeId: number, payload: EmployeeUpdateRequest) => store.updateEmployee(employeeId, payload),
    deleteEmployeeById: (employeeId: number) => store.deleteEmployeeById(employeeId),
    getEmployeeWorkDaysById: (employeeId: number) => store.getEmployeeWorkDaysById(employeeId),
};
